import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput, SelectInput } from "@/components/ui/Field";
import { API_BASE } from "@/lib/apiClient";
import {
  formatObjektadresseLine,
  listGeschaeftspartnerObjektadressen,
  listKontaktKunden,
  type Objektadresse,
} from "@/lib/kontakte";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  createSOrdner,
  deleteSOrdner,
  downloadAuthenticatedFile,
  fetchSOrdnerAuftragPrompt,
  getSOrdnerSharepointUrl,
  importSOrdnerAuftragExcel,
  importSOrdnerExcel,
  linkSOrdnerSharepoint,
  createSOrdnerSharepoint,
  listKunden,
  listKundenGeschaeftspartner,
  listSOrdner,
  sOrdnerAuftragTemplateDownloadUrl,
  sOrdnerAuftragTemplateFilename,
  sOrdnerTemplateDownloadUrl,
  updateSOrdner,
  type SOrdnerAuftragImportResult,
  getHeroCustomerCodeMap,
  listHeroKunden,
  type KundeOut,
  type SOrdnerOut,
} from "../../lib/settingsApi";

interface FolderCustomerOption {
  key: string;
  label: string;
  heroCustomerId: number | null;
  geschaeftspartnerId: number | null;
}

interface SOrdnerForm {
  nummer: string;
  beschreibung: string;
  jahr: string;
  kunde_id: string;
  hero_customer_id: string;
  geschaeftspartner_id: string;
  hero_address_id: string;
  ort_name_text: string;
  last_auftrag_nummer: string;
}

function objektadresseOptionLabel(addr: Objektadresse): string {
  const title = (addr.title || "").trim();
  const line = formatObjektadresseLine(addr);
  if (!title) return line;
  if (!line || line === "—" || line === title) return title;
  return `${title} – ${line}`;
}

const EMPTY: SOrdnerForm = {
  nummer: "",
  beschreibung: "",
  jahr: String(new Date().getFullYear()),
  kunde_id: "",
  hero_customer_id: "",
  geschaeftspartner_id: "",
  hero_address_id: "",
  ort_name_text: "",
  last_auftrag_nummer: "0",
};

export function SOrdnerSection() {
  const [rows, setRows] = useState<SOrdnerOut[]>([]);
  const [kunden, setKunden] = useState<KundeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<SOrdnerForm>(EMPTY);
  const [formBaseline, setFormBaseline] = useState<SOrdnerForm>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState<number | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<{
    variant: "success" | "warning" | "error";
    summary: string;
    lines: string[];
  } | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptTitle, setPromptTitle] = useState("");
  const [promptText, setPromptText] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);
  const auftragFileRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const [folderCustomers, setFolderCustomers] = useState<FolderCustomerOption[]>([]);
  const [folderCustomersLoading, setFolderCustomersLoading] = useState(false);
  const folderCustomerRequest = useRef(0);
  const [addresses, setAddresses] = useState<Objektadresse[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [pinnedAddress, setPinnedAddress] = useState<{ id: number; label: string } | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [list, k] = await Promise.all([listSOrdner(), listKunden()]);
      setRows(list);
      setKunden(k.sort((a, b) => a.code - b.code));
    } catch (err) {
      setError(err instanceof Error ? err.message : "S-Ordner konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["sordner", "kunden"],
    paused: formOpen,
    onChange: () => void reload({ silent: true }),
  });

  async function loadFolderCustomers(kundeId: string) {
    const k = kunden.find((x) => String(x.id) === kundeId);
    if (!k) {
      setFolderCustomers([]);
      setFolderCustomersLoading(false);
      return;
    }
    const requestId = ++folderCustomerRequest.current;
    setFolderCustomersLoading(true);
    try {
      const [assigned, map, allHero] = await Promise.all([
        listKundenGeschaeftspartner(k.id).catch(() => []),
        getHeroCustomerCodeMap().catch(() => []),
        listHeroKunden().catch(() => []),
      ]);
      if (folderCustomerRequest.current !== requestId) return;
      const options: FolderCustomerOption[] = [];
      const seenHero = new Set<number>();
      for (const gp of assigned) {
        if (gp.is_deleted) continue;
        const heroId = gp.hero_contact_id ?? null;
        if (heroId != null) seenHero.add(heroId);
        const name = (gp.firmenname || "").trim() || `App-Kunde ${gp.id}`;
        options.push({
          key: `gp:${gp.id}`,
          label: name,
          heroCustomerId: heroId,
          geschaeftspartnerId: gp.id,
        });
      }
      const codes = map.filter((entry) => entry.code === k.code);
      for (const hero of allHero) {
        if (!codes.some((entry) => entry.hero_contact_id === hero.id)) continue;
        if (seenHero.has(hero.id)) continue;
        const person = `${hero.first_name || ""} ${hero.last_name || ""}`.trim();
        const label = [hero.company_name, person].filter(Boolean).join(" – ");
        options.push({
          key: `hero:${hero.id}`,
          label: label || `Hero ${hero.id}`,
          heroCustomerId: hero.id,
          geschaeftspartnerId: null,
        });
      }
      options.sort((a, b) => a.label.localeCompare(b.label, "de"));
      setFolderCustomers(options);
    } finally {
      if (folderCustomerRequest.current === requestId) setFolderCustomersLoading(false);
    }
  }

  useEffect(() => {
    if (form.kunde_id) {
      void loadFolderCustomers(form.kunde_id);
      return;
    }
    folderCustomerRequest.current += 1;
    setFolderCustomers([]);
    setFolderCustomersLoading(false);
  }, [form.kunde_id, kunden]);

  useEffect(() => {
    if (form.geschaeftspartner_id || !form.hero_customer_id) return;
    const match = folderCustomers.find(
      (option) =>
        option.geschaeftspartnerId != null &&
        option.heroCustomerId != null &&
        String(option.heroCustomerId) === form.hero_customer_id,
    );
    if (!match?.geschaeftspartnerId) return;
    const gpId = String(match.geschaeftspartnerId);
    setForm((current) =>
      current.geschaeftspartner_id ? current : { ...current, geschaeftspartner_id: gpId },
    );
    setFormBaseline((current) =>
      current.geschaeftspartner_id ? current : { ...current, geschaeftspartner_id: gpId },
    );
  }, [folderCustomers, form.geschaeftspartner_id, form.hero_customer_id]);

  useEffect(() => {
    const gpId = parseInt(form.geschaeftspartner_id, 10);
    const heroId = parseInt(form.hero_customer_id, 10);
    const hasGp = Number.isFinite(gpId);
    const hasHero = Number.isFinite(heroId);
    if (!hasGp && !hasHero) {
      setAddresses([]);
      setAddressesLoading(false);
      return;
    }
    const kunde = kunden.find((row) => String(row.id) === form.kunde_id);
    let cancelled = false;
    setAddressesLoading(true);
    void (async () => {
      try {
        let resolvedGpId = hasGp ? gpId : null;
        if (resolvedGpId == null && hasHero) {
          const byHero = await listKontaktKunden({ limit: 5, hero_contact_id: heroId });
          resolvedGpId = byHero.items?.[0]?.id ?? null;
          if (resolvedGpId == null && kunde?.code != null) {
            const byCode = await listKontaktKunden({ limit: 5, kundencode: kunde.code });
            resolvedGpId = byCode.items?.[0]?.id ?? null;
          }
        }
        const list =
          resolvedGpId != null ? await listGeschaeftspartnerObjektadressen(resolvedGpId) : [];
        if (!cancelled) setAddresses(list || []);
      } catch {
        if (!cancelled) setAddresses([]);
      } finally {
        if (!cancelled) setAddressesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.geschaeftspartner_id, form.hero_customer_id, form.kunde_id, kunden]);

  function openCreate() {
    setEditId(null);
    setPinnedAddress(null);
    setForm(EMPTY);
    setFormBaseline(EMPTY);
    setFormError(null);
    setFormOpen(true);
  }

  async function openEdit(row: SOrdnerOut) {
    setEditId(row.id);
    const initial: SOrdnerForm = {
      nummer: row.nummer,
      beschreibung: row.beschreibung,
      jahr: row.jahr != null ? String(row.jahr) : String(new Date().getFullYear()),
      kunde_id: row.kunde_id != null ? String(row.kunde_id) : "",
      hero_customer_id: row.hero_customer_id != null ? String(row.hero_customer_id) : "",
      geschaeftspartner_id:
        row.geschaeftspartner_id != null ? String(row.geschaeftspartner_id) : "",
      hero_address_id: row.hero_address_id != null ? String(row.hero_address_id) : "",
      ort_name_text: row.ort_name_text || "",
      last_auftrag_nummer: row.last_auftrag_nummer != null ? String(row.last_auftrag_nummer) : "0",
    };
    setForm(initial);
    setFormBaseline(initial);
    setPinnedAddress(
      row.hero_address_id != null
        ? {
            id: row.hero_address_id,
            label: row.ort_name?.trim() || `Adresse #${row.hero_address_id}`,
          }
        : null,
    );
    setFormError(null);
    setFormOpen(true);
  }

  const addressOptions = useMemo(() => {
    const options = addresses.map((addr) => ({
      id: addr.address_id,
      label: objektadresseOptionLabel(addr),
    }));
    const selectedId = parseInt(form.hero_address_id, 10);
    if (
      pinnedAddress &&
      pinnedAddress.id === selectedId &&
      !options.some((option) => option.id === pinnedAddress.id)
    ) {
      options.unshift(pinnedAddress);
    }
    return options;
  }, [addresses, form.hero_address_id, pinnedAddress]);

  function buildBody(): Parameters<typeof createSOrdner>[0] {
    return {
      nummer: form.nummer.trim(),
      beschreibung: form.beschreibung.trim(),
      kunde_id: form.kunde_id ? parseInt(form.kunde_id, 10) : null,
      jahr: form.jahr ? parseInt(form.jahr, 10) : null,
      hero_customer_id: form.hero_customer_id ? parseInt(form.hero_customer_id, 10) : null,
      geschaeftspartner_id: form.geschaeftspartner_id
        ? parseInt(form.geschaeftspartner_id, 10)
        : null,
      hero_address_id: form.hero_address_id ? parseInt(form.hero_address_id, 10) : null,
      ort_name_text: form.ort_name_text.trim() || null,
      last_auftrag_nummer: parseInt(form.last_auftrag_nummer, 10) || 0,
    };
  }

  async function handleSave() {
    if (!form.nummer.trim() || !form.beschreibung.trim()) {
      setFormError("S-Nummer und Beschreibung sind Pflichtfelder.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const body = buildBody();
      if (editId) await updateSOrdner(editId, body);
      else await createSOrdner(body);
      setFormOpen(false);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row: SOrdnerOut) {
    if (!window.confirm(`S-Ordner „${row.nummer}" wirklich löschen?`)) return;
    try {
      await deleteSOrdner(row.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  function formatAuftragImport(result: SOrdnerAuftragImportResult) {
    const rows = Array.isArray(result.rows) ? result.rows : [];
    const failed = rows.filter((row) => !row.ok);
    const uncertain = rows.filter((row) => row.ok && (row.warnings?.length ?? 0) > 0);
    const lines = [...failed, ...uncertain]
      .slice(0, 8)
      .map((row) => `Zeile ${row.line}: ${row.message || "bitte prüfen"}`);
    const variant = failed.length > 0 || uncertain.length > 0 ? "warning" : "success";
    const summary = `${result.ok ?? 0} importiert, ${result.fail ?? 0} nicht übernommen${
      uncertain.length ? `, ${uncertain.length} mit unsicherer Zuordnung` : ""
    }.`;
    return { variant, summary, lines } as const;
  }

  async function handleAuftragImport(sordnerId: number, file: File | null) {
    if (!file) return;
    setImportingId(sordnerId);
    setImportReport(null);
    setError(null);
    try {
      const result = await importSOrdnerAuftragExcel(sordnerId, file);
      setImportReport(formatAuftragImport(result));
      await reload({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Auftrags-Import fehlgeschlagen.");
    } finally {
      setImportingId(null);
      const input = auftragFileRefs.current[sordnerId];
      if (input) input.value = "";
    }
  }

  async function openAuftragPrompt(row: SOrdnerOut) {
    setPromptOpen(true);
    setPromptTitle(`KI-Prompt für ${row.nummer}`);
    setPromptText("");
    setPromptError(null);
    setPromptCopied(false);
    setPromptLoading(true);
    try {
      const data = await fetchSOrdnerAuftragPrompt(row.id);
      setPromptText(data.prompt);
    } catch (err) {
      setPromptError(err instanceof Error ? err.message : "Prompt konnte nicht geladen werden.");
    } finally {
      setPromptLoading(false);
    }
  }

  async function copyAuftragPrompt() {
    if (!promptText) return;
    try {
      await navigator.clipboard.writeText(promptText);
      setPromptCopied(true);
      setPromptError(null);
    } catch {
      promptRef.current?.focus();
      promptRef.current?.select();
      setPromptError("Automatisches Kopieren ist hier nicht möglich. Der Text ist markiert.");
    }
  }

  async function downloadTemplate(key: string, url: string, filename: string) {
    setDownloadingKey(key);
    setError(null);
    try {
      await downloadAuthenticatedFile(url, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download fehlgeschlagen.");
    } finally {
      setDownloadingKey(null);
    }
  }

  async function handleImport(file: File) {
    try {
      await importSOrdnerExcel(file);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import fehlgeschlagen.");
    }
  }

  async function openSharepoint(row: SOrdnerOut) {
    const url = await getSOrdnerSharepointUrl(row.id);
    if (url) window.open(url, "_blank", "noopener");
    else window.open(`${API_BASE}/sordner/${row.id}/sharepoint-redirect`, "_blank", "noopener");
  }

  const yearOptions = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 3 + i);

  return (
    <Card
      title="S-Ordner"
      description="S-Ordner verwalten. Alte Aufträge kommen über die Auftrags-Vorlage in der Zeile."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            loading={downloadingKey === "ordner"}
            onClick={() =>
              void downloadTemplate("ordner", sOrdnerTemplateDownloadUrl(), "s-ordner-vorlage.xlsx")
            }
          >
            Ordner-Vorlage
          </Button>
          <label className="eh-btn eh-btn-secondary inline-flex h-10 cursor-pointer items-center px-4 text-sm">
            Excel importieren
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImport(f);
                e.target.value = "";
              }}
            />
          </label>
          <Button type="button" onClick={openCreate}>
            Neu
          </Button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      <Alert variant="info" title="Alte S-Ordner-Aufträge" className="mb-3">
        In der Zeile die Auftrags-Vorlage laden. Sie enthält den Kunden aus der Kundendatenbank
        mit dem Firmennamen, den Objektadressen und den Ansprechpartnern. In der Spalte Objektadresse die
        Adresse aus der Liste wählen. Objekt-ID, Straße, PLZ, Ort und die Kundenfelder füllt
        die Vorlage per Formel. Den KI-Prompt zusammen mit dieser Datei und dem gescannten
        Inhaltsverzeichnis an die KI geben. Orange markierte Zellen prüfen, danach die Datei
        importieren. Lieferscheinnummer, Rechnungsnummer, allgemeine Infos und Schlagwörter
        stehen am Projekt. Mehrere Schlagwörter in der Spalte mit Komma trennen. In der
        Projektliste heißen die Spalten Lieferscheinnr., Rechnungsnr. und Allg. Infos.
      </Alert>
      {error && (
        <Alert variant="error" title="Fehler" className="mb-3">
          {error}
        </Alert>
      )}
      {importReport && (
        <Alert variant={importReport.variant} title="Auftrags-Import" className="mb-3">
          <p>{importReport.summary}</p>
          {importReport.lines.length > 0 ? (
            <ul className="mt-2 list-disc pl-5">
              {importReport.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      )}
      {!loading && (
        <Table<SOrdnerOut>
          rowKey={(r) => r.id}
          rows={rows}
          stickyEndColumnCount={1}
          columns={[
            {
              key: "nummer",
              header: "S-Nummer",
              sortable: true,
              sortValue: (r) => r.nummer,
              render: (r) => r.nummer,
            },
            {
              key: "beschreibung",
              header: "Beschreibung",
              sortable: true,
              sortValue: (r) => r.beschreibung,
              render: (r) => r.beschreibung,
            },
            {
              key: "kunde",
              header: "Kunde",
              sortable: true,
              sortValue: (r) => r.kunden_name || "",
              render: (r) => r.kunden_name || "—",
            },
            {
              key: "jahr",
              header: "Jahr",
              sortable: true,
              sortValue: (r) => r.jahr ?? -1,
              render: (r) => r.jahr ?? "—",
            },
            {
              key: "last",
              header: "Letzte Auftrags-Nr.",
              sortable: true,
              sortValue: (r) => r.last_auftrag_nummer ?? 0,
              render: (r) => r.last_auftrag_nummer ?? 0,
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex flex-wrap justify-end gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={downloadingKey === `auftrag-${r.id}`}
                    onClick={() =>
                      void downloadTemplate(
                        `auftrag-${r.id}`,
                        sOrdnerAuftragTemplateDownloadUrl(r.id),
                        sOrdnerAuftragTemplateFilename(r.nummer),
                      )
                    }
                  >
                    Auftrags-Vorlage
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={importingId === r.id}
                    onClick={() => auftragFileRefs.current[r.id]?.click()}
                  >
                    Aufträge importieren
                  </Button>
                  <input
                    ref={(el) => {
                      auftragFileRefs.current[r.id] = el;
                    }}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => void handleAuftragImport(r.id, e.target.files?.[0] ?? null)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => void openAuftragPrompt(r)}
                  >
                    KI-Prompt
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(r)}>
                    Bearbeiten
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => openSharepoint(r)}>
                    SharePoint
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await linkSOrdnerSharepoint(r.id);
                        await reload();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : "Verknüpfen fehlgeschlagen.");
                      }
                    }}
                  >
                    Verknüpfen
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await createSOrdnerSharepoint(r.id);
                        await reload();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
                      }
                    }}
                  >
                    SP anlegen
                  </Button>
                  <Button type="button" size="sm" variant="danger" onClick={() => handleDelete(r)}>
                    Löschen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        dirty={hasFormChanges(form, formBaseline)}
        title={editId ? "S-Ordner bearbeiten" : "Neuen S-Ordner anlegen"}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave} loading={saving}>
              Speichern
            </Button>
          </>
        }
      >
        {formError && (
          <Alert variant="error" title="Fehler" className="mb-3">
            {formError}
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="S-Nummer"
            required
            value={form.nummer}
            onChange={(e) => setForm((f) => ({ ...f, nummer: e.target.value }))}
          />
          <TextInput
            label="Beschreibung"
            required
            value={form.beschreibung}
            onChange={(e) => setForm((f) => ({ ...f, beschreibung: e.target.value }))}
          />
          <SelectInput
            label="Erstellungsjahr"
            value={form.jahr}
            onChange={(e) => setForm((f) => ({ ...f, jahr: e.target.value }))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </SelectInput>
          <SelectInput
            label="Kunde (Ernsthaus)"
            value={form.kunde_id}
            onChange={(e) => {
              setPinnedAddress(null);
              setForm((f) => ({
                ...f,
                kunde_id: e.target.value,
                hero_customer_id: "",
                geschaeftspartner_id: "",
                hero_address_id: "",
              }));
            }}
          >
            <option value="">– bitte wählen –</option>
            {kunden.map((k) => (
              <option key={k.id} value={k.id}>
                {k.code} – {k.name}
              </option>
            ))}
          </SelectInput>
          <div>
            <SelectInput
              label="Kunde (App)"
              value={
                form.geschaeftspartner_id
                  ? `gp:${form.geschaeftspartner_id}`
                  : form.hero_customer_id
                    ? `hero:${form.hero_customer_id}`
                    : ""
              }
              onChange={(e) => {
                const option = folderCustomers.find((entry) => entry.key === e.target.value);
                setPinnedAddress(null);
                setForm((f) => ({
                  ...f,
                  geschaeftspartner_id:
                    option?.geschaeftspartnerId != null ? String(option.geschaeftspartnerId) : "",
                  hero_customer_id:
                    option?.heroCustomerId != null ? String(option.heroCustomerId) : "",
                  hero_address_id: "",
                }));
              }}
              disabled={!form.kunde_id || folderCustomersLoading}
            >
              <option value="">
                {folderCustomersLoading ? "lädt …" : "– bitte wählen –"}
              </option>
              {folderCustomers.map((customer) => (
                <option key={customer.key} value={customer.key}>
                  {customer.label}
                </option>
              ))}
            </SelectInput>
            {form.kunde_id && !folderCustomersLoading && folderCustomers.length === 0 ? (
              <p className="mt-1 text-xs text-label-secondary">
                Diesem Kundencode ist kein App-Kunde zugewiesen.
              </p>
            ) : null}
          </div>
          <div>
            <SelectInput
              label="Objektadresse"
              value={form.hero_address_id}
              onChange={(e) => setForm((f) => ({ ...f, hero_address_id: e.target.value }))}
              disabled={
                (!form.geschaeftspartner_id && !form.hero_customer_id) || addressesLoading
              }
            >
              <option value="">{addressesLoading ? "lädt …" : "– bitte wählen –"}</option>
              {addressOptions.map((addr) => (
                <option key={addr.id} value={addr.id}>
                  {addr.label}
                </option>
              ))}
            </SelectInput>
            {(form.geschaeftspartner_id || form.hero_customer_id) &&
            !addressesLoading &&
            addressOptions.length === 0 ? (
              <p className="mt-1 text-xs text-label-secondary">
                Keine Objektadressen in den Kontakten hinterlegt.
              </p>
            ) : null}
          </div>
          <TextInput
            label="Ort Namenszusatz"
            className="md:col-span-2"
            value={form.ort_name_text}
            onChange={(e) => setForm((f) => ({ ...f, ort_name_text: e.target.value }))}
          />
          <TextInput
            label="Letzte Auftrags-Nr."
            type="number"
            min={0}
            value={form.last_auftrag_nummer}
            onChange={(e) => setForm((f) => ({ ...f, last_auftrag_nummer: e.target.value }))}
          />
        </div>
      </Dialog>

      <Dialog
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        title={promptTitle || "KI-Prompt"}
        size="3xl"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setPromptOpen(false)}>
              Schließen
            </Button>
            <Button type="button" onClick={() => void copyAuftragPrompt()} disabled={!promptText}>
              {promptCopied ? "Kopiert" : "Prompt kopieren"}
            </Button>
          </>
        }
      >
        <p className="text-sm text-label-secondary">
          Prompt kopieren und zusammen mit der Auftrags-Vorlage und dem Scan des
          Inhaltsverzeichnisses an die KI geben. Die KI trägt die Aufträge ins Blatt „Aufträge“
          ein und färbt unsichere Zellen orange. Diese Zellen vor dem Import prüfen.
        </p>
        {promptLoading ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Lädt …
          </div>
        ) : null}
        {promptError ? (
          <Alert variant="error" title="Fehler" className="mt-3">
            {promptError}
          </Alert>
        ) : null}
        <textarea
          ref={promptRef}
          readOnly
          value={promptText}
          rows={22}
          className="eh-input mt-3 w-full font-mono text-xs"
          aria-label="KI-Prompt"
        />
      </Dialog>
    </Card>
  );
}
